import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Building2, GraduationCap, Languages, Stethoscope } from "lucide-react";
import { BrandMark } from "@/components/AppShell";
import TrustBadge from "@/components/TrustBadge";
import TrustRadar from "@/components/TrustRadar";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function DoctorPublicProfile() {
  const { id } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [trust, setTrust] = useState(null);

  useEffect(() => {
    (async () => {
      const [d, t] = await Promise.all([
        api.get(`/doctors/${id}`),
        api.get(`/trust-index/doctor/${id}`),
      ]);
      setDoctor(d.data);
      setTrust(t.data);
    })();
  }, [id]);

  if (!doctor) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <Link to="/directory" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1">
            <ArrowLeft size={14} /> Back to directory
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-lg border border-border bg-white p-6 lg:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="overline">Verified · {doctor.specialty}</div>
                <h1 className="mt-2 font-serif text-4xl text-primary" data-testid="doctor-name">{doctor.name}</h1>
                <p className="text-muted-foreground mt-1">{doctor.qualification}</p>
              </div>
              <TrustBadge tier={doctor.tier} score={doctor.trust_score} />
            </div>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <InfoStat icon={Stethoscope} label="Experience" value={`${doctor.experience_years} yrs`} />
              <InfoStat icon={MapPin} label="City" value={doctor.city} />
              <InfoStat icon={Building2} label="Affiliation" value={doctor.hospital_affiliation || "—"} />
              <InfoStat icon={GraduationCap} label="Reg. No." value={doctor.registration_number || "—"} />
            </div>
            <div className="mt-6 pt-6 border-t border-border">
              <div className="overline mb-2">About</div>
              <p className="text-sm leading-relaxed text-muted-foreground">{doctor.bio || "This doctor has not added a bio yet."}</p>
            </div>
            <div className="mt-4 flex items-center gap-2 text-sm">
              <Languages size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">Languages:</span>
              <span>{(doctor.languages || []).join(", ") || "English"}</span>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-white p-6">
            <div className="overline mb-3">Trust Index breakdown</div>
            {trust && <TrustRadar criteria={trust.criteria} />}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-primary text-primary-foreground p-6">
            <div className="overline text-primary-foreground/70">Consultation fee</div>
            <div className="mt-2 font-serif text-4xl">₹ {doctor.consultation_fee || 500}</div>
            <div className="mt-1 text-sm text-primary-foreground/80">In-person or teleconsult</div>
            <Link to="/register">
              <Button className="mt-5 w-full bg-white text-primary hover:bg-white/90" data-testid="doctor-book-button">
                Book appointment
              </Button>
            </Link>
          </div>

          {trust && (
            <div className="rounded-lg border border-border bg-white p-6">
              <div className="overline mb-3">Improvement roadmap</div>
              <ul className="space-y-3">
                {trust.roadmap.map((r) => (
                  <li key={r.area} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-primary">{r.area}</span>
                      <span className="text-xs text-muted-foreground">{r.current} → {r.target}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{r.recommendation}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoStat({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon size={12} /> {label}
      </div>
      <div className="mt-1 text-primary font-medium">{value}</div>
    </div>
  );
}
